// CaptchaCheck.js — Casilla «No soy un robot» propia de la plataforma.
//
// Es el suplente de Cloudflare Turnstile para los proyectos que todavía no
// tienen llaves de Cloudflare: la persona solo marca la casilla. Detrás, el
// navegador resuelve el reto que manda `api/captcha.php` (buscar un número que
// haga que el sha256 empiece por ceros) y el servidor lo comprueba al enviar el
// formulario. Un clic para quien escribe; trabajo de máquina para quien quiera
// mandar miles de correos automáticos.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { captchaService } from '../../../services/api';
import CaptchaSimple from './CaptchaSimple';

const Caja = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  box-sizing: border-box;
  padding: 12px 14px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.06);
  color: ${p => p.$color};
  font-size: 15px;
  cursor: ${p => (p.$estado === 'listo' ? 'pointer' : 'default')};
  transition: border-color .2s ease, background .2s ease;
  &:hover { border-color: ${p => (p.$estado === 'listo' ? 'rgba(255,255,255,.4)' : 'rgba(255,255,255,.18)')}; }
  &:focus-visible { outline: 2px solid #f0abfc; outline-offset: 2px; }

  .marca {
    flex: none;
    width: 24px; height: 24px;
    display: grid; place-items: center;
    border: 1px solid rgba(255, 255, 255, 0.5);
    border-radius: 4px;
    background: #fff;
  }
  .marca svg { width: 16px; height: 16px; }
  .giro {
    width: 16px; height: 16px;
    border: 2px solid rgba(0, 0, 0, .18);
    border-top-color: #7e22ce;
    border-radius: 50%;
    animation: captcha-giro .7s linear infinite;
  }
  @keyframes captcha-giro { to { transform: rotate(360deg); } }
  @media (prefers-reduced-motion: reduce) { .giro { animation-duration: 2s; } }

  .texto { flex: 1; text-align: left; line-height: 1.25; }
  .aviso { display: block; font-size: 12.5px; opacity: .75; margin-top: 2px; }
`;

/** Busca el número que resuelve el reto. Cede el hilo cada tanto para no congelar la página. */
const resolverReto = async (reto, dificultad, vivo) => {
  const ceros = '0'.repeat(dificultad);
  const bytes = Math.ceil(dificultad / 2);
  const codificador = new TextEncoder();
  for (let n = 0; n < 50000000; n++) {
    const resumen = await window.crypto.subtle.digest('SHA-256', codificador.encode(reto + n));
    const inicio = Array.from(new Uint8Array(resumen).slice(0, bytes))
      .map(b => b.toString(16).padStart(2, '0')).join('');
    if (inicio.startsWith(ceros)) return String(n);
    if (n % 1500 === 0) {
      if (!vivo.current) return null;
      await new Promise(r => setTimeout(r, 0));
    }
  }
  return null;
};

const CaptchaCheck = ({ onCambio, color = '#ddcafc', reinicio = 0, etiqueta = 'No soy un robot' }) => {
  const [reto, setReto] = useState(null);
  const [estado, setEstado] = useState('listo'); // listo | trabajando | ok | error
  const vivo = useRef(true);

  const pedir = useCallback(() => {
    setEstado('listo');
    onCambio?.({ token: '', respuesta: '' });
    captchaService.obtener('check')
      .then(r => setReto(r.data))
      .catch(() => { setReto(null); setEstado('error'); });
  // `onCambio` cambia en cada render del formulario; el reto solo se pide al montar o al reiniciar.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    pedir();
    vivo.current = true;
    return () => { vivo.current = false; };
  }, [pedir, reinicio]);

  const marcar = async () => {
    if (!reto || estado !== 'listo') return;
    setEstado('trabajando');
    const n = await resolverReto(reto.reto, reto.dificultad || 4, vivo);
    if (!vivo.current) return;
    if (n === null) { setEstado('error'); return; }
    onCambio?.({ token: reto.token, respuesta: n });
    setEstado('ok');
  };

  // Sin cifrado en el navegador (páginas sin https o navegadores viejos) se
  // vuelve a la pregunta de siempre, que no necesita nada especial.
  if (!window.crypto?.subtle) {
    return <CaptchaSimple onCambio={onCambio} color={color} reinicio={reinicio} />;
  }
  if (!reto && estado !== 'error') return null;

  return (
    <Caja
      $color={color}
      $estado={estado}
      role="checkbox"
      aria-checked={estado === 'ok'}
      aria-busy={estado === 'trabajando'}
      tabIndex={0}
      onClick={estado === 'error' ? pedir : marcar}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); (estado === 'error' ? pedir : marcar)(); } }}
    >
      <span className="marca">
        {estado === 'trabajando' && <span className="giro" />}
        {estado === 'ok' && (
          <svg viewBox="0 0 20 20" aria-hidden="true">
            <path d="M3 10.5l4.5 4.5L17 5.5" fill="none" stroke="#16a34a" strokeWidth="2.6"
              strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>
      <span className="texto">
        {estado === 'ok' ? 'Verificado' : estado === 'error' ? 'No se pudo verificar' : etiqueta}
        <span className="aviso">
          {estado === 'trabajando' ? 'Comprobando…'
            : estado === 'error' ? 'Toca aquí y reintenta'
            : 'Protección antispam'}
        </span>
      </span>
    </Caja>
  );
};

export default CaptchaCheck;
